import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a zoom dialog.
 * Images are stacked vertically; the dialog itself scrolls (Horizon default).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  /**
   * Geen strikte ref-validatie: bij `importNode`/`replaceWith` kunnen nested refs kort ontbreken;
   * `media[]` ontbreekt bij lege zoom-inhoud — niet laten crashen op mount.
   */
  requiredRefs = [];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #isOpening = false;
  #lastZoomMediaIndex = -1;

  connectedCallback() {
    super.connectedCallback();
    if (!this.refs.dialog) return;
    if (!Array.isArray(this.refs.media)) this.refs.media = [];
    // Desktop: dialog scrolls vertically. Mobile: .dialog-zoomed-gallery scrolls horizontally.
    this.refs.dialog.addEventListener('scroll', this.handleScroll, { passive: true });
    this.querySelector('.dialog-zoomed-gallery')?.addEventListener('scroll', this.handleScroll, {
      passive: true,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('scroll', this.handleScroll);
    this.querySelector('.dialog-zoomed-gallery')?.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    // Set flag to prevent scroll handler from overwriting thumbnail selection during opening
    this.#isOpening = true;

    const { dialog, media, thumbnails } = this.refs;
    if (!dialog) {
      this.#isOpening = false;
      return;
    }

    const targetImage = media?.[index];
    const targetThumbnail = thumbnails?.children?.[index];

    const open = () => {
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant', block: 'nearest', inline: 'start' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      await new Promise((resolve) => setTimeout(resolve, 100));
      await this.selectThumbnail(index, { behavior: 'instant' });
      this.#isOpening = false;
      return;
    }

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    await new Promise((resolve) => setTimeout(resolve, 100));
    await this.selectThumbnail(index, { behavior: 'instant' });
    this.#isOpening = false;
  }

  /**
   * Resets pinch/drag zoom on all image wrappers in this dialog.
   */
  #resetDragZoomWrappers() {
    this.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => {
      if (typeof wrapper.resetZoomState === 'function') {
        wrapper.resetZoomState();
      }
    });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Updates the active thumbnail when the corresponding image is visible while scrolling.
   */
  handleScroll = debounce(async () => {
    if (this.#isOpening) return;

    const { media, thumbnails } = this.refs;
    if (!media?.length || !thumbnails) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });

    if (activeIndex !== this.#lastZoomMediaIndex) {
      this.#lastZoomMediaIndex = activeIndex;
      this.#resetDragZoomWrappers();
      this.#loadHighResolutionImage(mostVisibleElement);
      this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
    }
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    const mostVisibleElement = media?.length ? await getMostVisibleElement(media) : null;
    if (!mostVisibleElement) return this.closeDialog();

    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    if (this.refs.thumbnails) {
      await onAnimationEnd(this.refs.thumbnails);
    }

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#lastZoomMediaIndex = -1;
    dialog?.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    this.close();
  }

  /**
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media?.[index]) return;
    this.#loadHighResolutionImage(media[index]);
  }

  /**
   * @param {number} index
   * @param {Object} options
   * @param {ScrollBehavior} options.behavior
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media, thumbnails } = this.refs;

    if (isNaN(index) || index < 0 || !media?.length || index >= media.length) return;
    if (thumbnails?.children?.length && index >= thumbnails.children.length) return;

    if (index !== this.#lastZoomMediaIndex) {
      this.#lastZoomMediaIndex = index;
      this.#resetDragZoomWrappers();
    }

    const targetThumbnail = thumbnails?.children?.[index];

    if (targetThumbnail instanceof HTMLElement) {
      Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
        button.setAttribute('aria-selected', `${i === index}`);
      });

      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = media[index];
    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
        block: 'nearest',
        inline: 'start',
      });
      this.#loadHighResolutionImage(targetImage);
    }

    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * @param {HTMLElement[]} elements
 * @returns {Promise<HTMLElement | null>}
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    if (!elements || !Array.isArray(elements) || elements.length === 0) {
      resolve(null);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
